import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  MenuItem,
  Box,
  Autocomplete,
  ListSubheader,
} from "@mui/material";

import { AuthContext } from "../../context/AuthProvider";
import { fetchWithInterceptor } from "../../utils/fetchWithInterceptor";

const INITIAL_VALUES = {
  name: "",
  level: "",
  parentTypeId: "",
  parentSubtypeId: "",
};

export default function TypeFormDialog({ open, onClose, onSave, rowId, productType }) {
  const [data, setData] = useState(INITIAL_VALUES);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    if (!open) return;

    if (rowId && productType) {
      setData({
        name: productType.name || "",
        level: productType.level || "",
        parentTypeId: productType.parentTypeId ?? "",
        parentSubtypeId: productType.parentSubtypeId ?? "",
      });
    } else {
      setData(INITIAL_VALUES);
    }
  }, [rowId, productType, open]);

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [typesRes, subtypesRes] = await Promise.all([
          fetchWithInterceptor("/api/product-types?level=type&isActive=true&limit=500", {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }),
          fetchWithInterceptor("/api/product-types?level=subtype&isActive=true&limit=500", {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }),
        ]);

        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setTypes(typesData.rows || []);
        }
        if (subtypesRes.ok) {
          const subtypesData = await subtypesRes.json();
          setSubtypes(subtypesData.rows || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, authContext]);

  const subtypesSortedByType = useMemo(() => {
    return [...subtypes].sort((a, b) => {
      const nameA = a.parentType?.name ?? "";
      const nameB = b.parentType?.name ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [subtypes]);

  const selectedType = useMemo(() => {
    if (!data.parentTypeId) return null;
    return types.find((t) => t.id === Number(data.parentTypeId)) ?? null;
  }, [types, data.parentTypeId]);

  const selectedSubtype = useMemo(() => {
    if (!data.parentSubtypeId) return null;
    return subtypes.find((s) => s.id === Number(data.parentSubtypeId)) ?? null;
  }, [subtypes, data.parentSubtypeId]);

  const handleClose = () => {
    setData(INITIAL_VALUES);
    onClose();
  };

  const handleLevelChange = (value) => {
    setData((prev) => ({
      ...prev,
      level: value,
      parentTypeId: "",
      parentSubtypeId: "",
    }));
  };

  const isValid = () => {
    if (!data.name.trim() || !data.level) return false;
    if (data.level === "subtype" && !selectedType) return false;
    if (data.level === "subsubtype" && !selectedSubtype) return false;
    return true;
  };

  const handleSave = () => {
    const payload = {
      name: data.name.trim(),
      level: data.level,
    };
    if (data.level === "subtype") payload.parentTypeId = Number(data.parentTypeId);
    if (data.level === "subsubtype") payload.parentSubtypeId = Number(data.parentSubtypeId);
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{rowId ? "Edit Type" : "Create Type"}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          {rowId
            ? "Update the type information."
            : "Fill out the information to create a new type, subtype, or subsubtype."}
        </DialogContentText>

        <Box
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <TextField
            label="Name"
            fullWidth
            required
            autoComplete="off"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            helperText="This field is required"
          />

          <TextField
            select
            label="Level"
            fullWidth
            required
            autoComplete="off"
            value={data.level}
            onChange={(e) => handleLevelChange(e.target.value)}
            helperText="This field is required"
          >
            <MenuItem value="">Select level</MenuItem>
            <MenuItem value="type">Type</MenuItem>
            <MenuItem value="subtype">Subtype</MenuItem>
            <MenuItem value="subsubtype">Subsubtype</MenuItem>
          </TextField>

          {data.level === "subtype" && (
            <Autocomplete
              options={types}
              getOptionLabel={(option) => option.name}
              value={selectedType}
              onChange={(_, newValue) =>
                setData({ ...data, parentTypeId: newValue ? newValue.id : "" })
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={loadingOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Type"
                  required
                  autoComplete="off"
                  helperText="Subtype must belong to a type"
                />
              )}
            />
          )}

          {data.level === "subsubtype" && (
            <Autocomplete
              options={subtypesSortedByType}
              groupBy={(option) => option.parentType?.name ?? ""}
              getOptionLabel={(option) => option.name}
              value={selectedSubtype}
              onChange={(_, newValue) =>
                setData({ ...data, parentSubtypeId: newValue ? newValue.id : "" })
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={loadingOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Subtype"
                  required
                  autoComplete="off"
                  helperText="Subsubtype must belong to a subtype"
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader
                    component="div"
                    sx={{
                      bgcolor: "background.default",
                      fontWeight: 800,
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              )}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
